import React, { useState, useRef, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Calendar } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  label: string;
  required?: boolean;
  placeholder?: string;
}

export default function DatePicker({
  value,
  onChange,
  label,
  required = false,
  placeholder = 'Select date'
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      setInputValue(formattedDate);
      onChange(formattedDate);
      setIsOpen(false);
    }
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={datePickerRef}>
      <label className="block text-sm font-medium text-gray-300 mb-1">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <input
          type="date"
          value={inputValue}
          onChange={handleInputChange}
          className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
          placeholder={placeholder}
          required={required}
          onClick={() => setIsOpen(true)}
        />
        <button
          type="button"
          onClick={toggleCalendar}
          className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
        >
          <Calendar className="h-5 w-5" />
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-10 mt-1">
          <ReactDatePicker
            selected={inputValue ? new Date(inputValue) : null}
            onChange={handleDateChange}
            inline
            calendarClassName="bg-gray-800 border border-gray-700 rounded-md shadow-lg text-white"
            dayClassName={date => 
              "hover:bg-primary hover:text-white text-center py-2 rounded-md"
            }
            wrapperClassName="date-picker"
          />
        </div>
      )}
    </div>
  );
}